import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductMockup from "@/components/marketing/ProductMockup";
import { track } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import { SOLUTION_PAGES } from "@/lib/content";

export default function Solutions() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = SOLUTION_PAGES[slug];

  useSEO(page ? {
    title: page.meta_title || page.title,
    description: page.meta_description || page.sub,
    path: `/solutions/${slug}`,
    jsonLd: breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Solutions", path: "/#segments" },
      { name: page.title.replace("Mashara Finance for ", ""), path: `/solutions/${slug}` },
    ]),
  } : { title: "Solution Not Found — Mashara Finance", path: `/solutions/${slug}`, noindex: true });

  if (!page) {
    return (
      <div className="py-32 text-center" data-testid="solution-not-found">
        <h1 className="font-display text-3xl font-bold text-slate-900">Solution not found</h1>
        <Link to="/" className="text-emerald-700 font-semibold mt-4 inline-block">Go home →</Link>
      </div>
    );
  }

  return (
    <div data-testid={`solution-${slug}`}>
      <section className="bg-gradient-to-b from-emerald-50/60 via-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 sm:pt-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-4">Solutions</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-slate-900" data-testid="solution-headline">{page.h1}</h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">{page.sub}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button data-testid="solution-trial-button" size="lg"
                onClick={() => { track("hero_cta_click", { location: `solution:${slug}` }); navigate("/signup"); }}
                className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6 text-base font-semibold">
                Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button data-testid="solution-demo-button" size="lg" variant="outline" onClick={() => navigate("/demo")}
                className="rounded-xl border-slate-300 px-8 py-6 text-base font-semibold text-slate-700 hover:text-emerald-700">
                Book a Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-8">{page.manages_heading || "Manage Everything in One Place"}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {page.manages.map((m) => (
                <div key={m} className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl p-4">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-sm font-medium text-slate-800">{m}</span>
                </div>
              ))}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-12 mb-6">{page.points_heading || "Why Teams Choose Mashara Finance"}</h2>
            <ul className="space-y-4">
              {page.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-700">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:sticky lg:top-24">
            <ProductMockup active="dashboard" alt={page.alt} />
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Explore This Solution With Your Own Data</h2>
          <p className="mt-3 text-slate-600">10-day free trial · Activated with a ₹1 verification payment · Cancel anytime</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Button data-testid="solution-bottom-trial-button" size="lg" onClick={() => navigate("/signup")}
              className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-8">Start Free Trial</Button>
            <Button data-testid="solution-bottom-demo-button" size="lg" variant="outline" onClick={() => navigate("/demo")}
              className="rounded-xl border-slate-300 text-slate-700 px-8">Book a Demo</Button>
          </div>
          {page.related && (
            <div className="mt-10 pt-8 border-t border-slate-200">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Related Pages</p>
              <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
                {page.related.map((r) => (
                  <Link key={r.to} to={r.to} data-testid={`solution-related-${r.to.split("/").pop()}`} className="text-sm font-medium text-emerald-700 hover:text-emerald-800">{r.label}</Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
